import React, { useMemo } from 'react';

import { useIntl } from 'react-intl-next';
import { graphql, useLazyLoadQuery } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';
import type { HoverEntityCardClickTarget } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics/entity-annotation-events';
import { isInferredProjectEntityAnnotationEnabled } from '@atlassian/conversation-assistant-utils/isEntityAnnotationExperimentEnabled';
import { LinkList } from '@atlassian/smart-knowledge-cards/link-list';

import type {
	RelatedContentSectionQuery,
	RelatedContentSectionQuery$data,
} from './__generated__/RelatedContentSectionQuery.graphql';
import messages from './inferredProjectCardMessages';
import { buildRelatedCypher } from './relatedContentCypher';

/** Broad content type used to guarantee visible coverage across documents, issues and PRs. */
export type RelatedContentGroup = 'document' | 'issue' | 'pullRequest';

/** A single piece of related content reduced to what the card renders: a link. */
export type RelatedContentLink = {
	/** Stable identity used for de-duplication (the node ARI). */
	id: string;
	/** http(s) URL. Guaranteed to be a web URL for every rendered link. */
	url: string;
	/** Human-readable title, or `undefined` when the graph has none. */
	title?: string;
	/** Relationship/entity timestamp used to sort most-recent-first, when known. */
	updatedAt?: number;
	/**
	 * Broad content type, used only to keep ranking diverse (one of each type before filling the
	 * rest by recency). Not rendered. Base sources passed by a consumer may omit it.
	 */
	group?: RelatedContentGroup;
};

type Nullable<T> = T | null | undefined;

type Edge = NonNullable<
	RelatedContentSectionQuery$data['graphStore_cypherQueryV2']
>['edges'][number];
type AriNode = Extract<
	Edge['node']['columns'][number]['value'],
	{ __typename: 'GraphStoreCypherQueryV2AriNode' }
>;
type Row = { kind?: string; node?: AriNode; timestamp?: number };

const optionalString = (value: Nullable<string>): string | undefined =>
	typeof value === 'string' && value.trim() ? value : undefined;

// Graph data is external input, so only http(s) URLs are ever rendered as links.
const toWebUrl = (value: Nullable<string>): string | undefined => {
	if (!value) {
		return undefined;
	}
	try {
		const { protocol } = new URL(value);
		return protocol === 'https:' || protocol === 'http:' ? value : undefined;
	} catch {
		return undefined;
	}
};

// Confluence `links.webUi` is relative to `links.base`; without a base there is no safe href.
const toConfluenceUrl = (
	baseUrl: Nullable<string>,
	webUiPath: Nullable<string>,
): string | undefined => {
	const base = optionalString(baseUrl);
	const webUi = optionalString(webUiPath);
	if (!base || !webUi) {
		return undefined;
	}
	return toWebUrl(`${base.replace(/\/$/, '')}${webUi.startsWith('/') ? '' : '/'}${webUi}`);
};

const toTimestamp = (value: Nullable<string | number>): number | undefined => {
	if (typeof value === 'number') {
		return Number.isFinite(value) && value > 0 ? value : undefined;
	}
	if (typeof value === 'string') {
		const parsed = Date.parse(value);
		return Number.isNaN(parsed) ? undefined : parsed;
	}
	return undefined;
};

const readRow = (edge: Edge): Row => {
	const row: Row = {};
	for (const column of edge.node.columns) {
		const value = column.value;
		if (column.key === 'kind' && value?.__typename === 'GraphStoreCypherQueryV2StringObject') {
			row.kind = value.stringValue;
		} else if (column.key === 'node' && value?.__typename === 'GraphStoreCypherQueryV2AriNode') {
			row.node = value;
		} else if (
			column.key === 'ts' &&
			value?.__typename === 'GraphStoreCypherQueryV2TimestampObject'
		) {
			row.timestamp = toTimestamp(value.timestampValue);
		}
	}
	return row;
};

const toLink = ({ node, timestamp }: Row): RelatedContentLink | undefined => {
	const data = node?.data;
	if (!node || !data) {
		return undefined;
	}
	switch (data.__typename) {
		case 'JiraIssue': {
			const url = toWebUrl(data.issueUrl);
			if (!url || !optionalString(data.issueKey)) {
				return undefined;
			}
			return {
				id: node.id,
				url,
				title: optionalString(data.issueSummary) ?? data.issueKey,
				updatedAt: toTimestamp(data.issueUpdated) ?? timestamp,
				group: 'issue',
			};
		}
		case 'ConfluencePage': {
			const url = toConfluenceUrl(data.pageLinks?.base, data.pageLinks?.webUi);
			const title = optionalString(data.pageTitle);
			return url && title
				? { id: node.id, url, title, updatedAt: timestamp, group: 'document' }
				: undefined;
		}
		case 'ConfluenceBlogPost': {
			const url = toConfluenceUrl(data.blogPostLinks?.base, data.blogPostLinks?.webUi);
			const title = optionalString(data.blogPostTitle);
			return url && title
				? { id: node.id, url, title, updatedAt: timestamp, group: 'document' }
				: undefined;
		}
		case 'ExternalPullRequest': {
			const url = toWebUrl(data.externalPullRequestUrl);
			const title = optionalString(data.externalPullRequestTitle);
			return url && title
				? { id: node.id, url, title, updatedAt: timestamp, group: 'pullRequest' }
				: undefined;
		}
		case 'DevOpsPullRequestDetails': {
			const url = toWebUrl(data.devOpsPullRequestUrl);
			const title = optionalString(data.devOpsPullRequestTitle);
			return url && title
				? { id: node.id, url, title, updatedAt: timestamp, group: 'pullRequest' }
				: undefined;
		}
		default:
			return undefined;
	}
};

// Every row kind maps to a link the same way; the kind only distinguishes rows for the graph query.
const RELATED_ROW_KINDS = new Set(['document', 'child', 'related', 'pullRequest']);

// Number of related links shown while the LinkList is collapsed, and the maximum shown once expanded.
const MAX_SOURCES = 2;
const EXPANDED_MAX_SOURCES = 5;

// How many related rows to fetch. Far larger than EXPANDED_MAX_SOURCES on purpose: the query orders
// by recency, so this returns the newest N, and we still need a pool big enough to survive de-dup and
// to contain each content type for coverage-aware ranking. Not a display limit.
const RELATED_CONTENT_FETCH_LIMIT = 100;

const rankByRecency = (links: readonly RelatedContentLink[]): RelatedContentLink[] =>
	[...links].sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0));

// Newest item from each content group first, then the rest by recency. Keeps one prolific type (or a
// batch of timestamp-less pull requests) from occupying every visible slot. Links without a group
// (e.g. consumer-supplied base sources) are ranked purely by recency among the "rest".
const rankByRecencyWithCoverage = (links: readonly RelatedContentLink[]): RelatedContentLink[] => {
	const firstOfGroup: RelatedContentLink[] = [];
	const rest: RelatedContentLink[] = [];
	const seenGroups = new Set<RelatedContentGroup>();
	for (const link of rankByRecency(links)) {
		if (link.group && !seenGroups.has(link.group)) {
			seenGroups.add(link.group);
			firstOfGroup.push(link);
		} else {
			rest.push(link);
		}
	}
	return [...firstOfGroup, ...rest];
};

// Reduces the Cypher query response to a deduped list of links, ranked so each content type is
// represented before the rest fill in by recency. Colocated with the query so the Relay field usage
// stays in this file; the section render test exercises it end to end.
const parseRelatedContent = (data: RelatedContentSectionQuery$data): RelatedContentLink[] => {
	const links: RelatedContentLink[] = [];
	const seen = new Set<string>();

	for (const edge of data.graphStore_cypherQueryV2?.edges ?? []) {
		const row = readRow(edge);
		if (!row.kind || !RELATED_ROW_KINDS.has(row.kind)) {
			continue;
		}
		const link = toLink(row);
		// De-dupe on node identity first, then on URL so the same target reached through different
		// relationships is only listed once.
		if (link && !seen.has(link.id) && !seen.has(link.url)) {
			seen.add(link.id);
			seen.add(link.url);
			links.push(link);
		}
	}

	return rankByRecencyWithCoverage(links);
};

const styles = cssMap({
	container: {
		paddingBlockStart: token('space.100'),
	},
	links: {
		width: '100%',
	},
});

export type RelatedContentSectionProps = {
	cloudId: string;
	/** The inferred project ARI whose related content should be loaded. */
	projectAri: string;
	/**
	 * The project's primary sources. Fetched related links that duplicate one of these (by id or url)
	 * are always dropped so the same item is never listed twice. Whether these are themselves rendered
	 * depends on `renderPrimarySources`.
	 */
	primarySources?: RelatedContentLink[];
	/**
	 * Whether to render `primarySources` in this section. Defaults to `true`: they are shown first,
	 * ahead of the fetched related content, and remain visible even when related content is empty or
	 * fails to load. Set to `false` when the sources are already shown elsewhere on the card (e.g.
	 * above an inferred, not-yet-promoted project) — they are then used only to de-duplicate related
	 * links.
	 */
	renderPrimarySources?: boolean;
	/** Whether to render the "Related content" heading. Hidden when merged under an existing label. */
	showHeading?: boolean;
	onInteraction?: (targetType: HoverEntityCardClickTarget) => void;
};

/**
 * Isolates loading the project's related content and rendering the resulting links. Runs the
 * second-hop Cypher query for the project (documents, child/related issues, and pull requests),
 * reduces the result to a list of URLs and renders them. Must be rendered inside a `<Suspense>`
 * (and, ideally, an error boundary) because the underlying query suspends while loading and can
 * fail. Renders nothing when there is no related content, so the rest of the card is unaffected.
 *
 * This is the seam where a dedicated URL-rendering component can be dropped in later: the derived
 * `urls`/`titles` are what would be handed to it; today the shared LinkList renders them.
 */
export const RelatedContentSection = ({
	cloudId,
	projectAri,
	primarySources,
	renderPrimarySources = true,
	showHeading = true,
	onInteraction,
}: RelatedContentSectionProps): React.ReactNode => {
	const { formatMessage } = useIntl();
	const data = useLazyLoadQuery<RelatedContentSectionQuery>(
		graphql`
			query RelatedContentSectionQuery(
				$cypherQuery: String!
				$queryContext: String!
				$first: Int!
			) {
				graphStore_cypherQueryV2(
					query: $cypherQuery
					queryContext: $queryContext
					version: V3
					first: $first
				) @optIn(to: "GraphStoreCypherQueryV2") {
					edges {
						node {
							columns {
								key
								value {
									__typename
									... on GraphStoreCypherQueryV2StringObject {
										stringValue: value
									}
									... on GraphStoreCypherQueryV2TimestampObject {
										timestampValue: value
									}
									... on GraphStoreCypherQueryV2AriNode {
										id
										data {
											__typename
											... on JiraIssue {
												issueKey: key
												issueSummary: summary
												issueUrl: webUrl
												issueUpdated: updated
											}
											... on ConfluencePage {
												pageTitle: title
												pageLinks: links {
													base
													webUi
												}
											}
											... on ConfluenceBlogPost {
												blogPostTitle: title
												blogPostLinks: links {
													base
													webUi
												}
											}
											... on ExternalPullRequest {
												externalPullRequestTitle: title
												externalPullRequestUrl: url
											}
											... on DevOpsPullRequestDetails {
												devOpsPullRequestTitle: title
												devOpsPullRequestUrl: url
											}
										}
									}
								}
							}
						}
					}
				}
			}
		`,
		{
			cypherQuery: buildRelatedCypher(projectAri),
			queryContext: `ari:cloud:platform::site/${cloudId}`,
			first: RELATED_CONTENT_FETCH_LIMIT,
		},
		{ fetchPolicy: 'store-or-network' },
	);

	const relatedLinks = useMemo(() => parseRelatedContent(data), [data]);

	// Base sources always de-duplicate the fetched related links (matched by id or url) so the same
	// item is never listed twice. When they are rendered here they come first and win the duplicate;
	// when they are shown elsewhere (renderPrimarySources === false) they only seed the de-dup set.
	// The LinkList never shows more than the expanded cap, so we trim to avoid handing it (and the
	// related count) more links than can ever be rendered.
	const visibleLinks = useMemo(() => {
		const sources = primarySources ?? [];
		const merged: RelatedContentLink[] = [];
		const seen = new Set<string>();
		if (!renderPrimarySources) {
			// Seed the de-dup set with the sources without adding them to the rendered list.
			for (const link of sources) {
				seen.add(link.id);
				seen.add(link.url);
			}
		}
		const candidates = renderPrimarySources ? [...sources, ...relatedLinks] : relatedLinks;
		for (const link of candidates) {
			if (seen.has(link.id) || seen.has(link.url)) {
				continue;
			}
			seen.add(link.id);
			seen.add(link.url);
			merged.push(link);
		}
		return merged.slice(0, EXPANDED_MAX_SOURCES);
	}, [primarySources, renderPrimarySources, relatedLinks]);

	if (visibleLinks.length === 0) {
		return null;
	}

	const urls = visibleLinks.map((link) => link.url);
	const titles = visibleLinks.map((link) => link.title ?? '');

	return (
		<Box xcss={styles.container}>
			<Stack space="space.075">
				{showHeading ? (
					<Text color="color.text" size="small" weight="medium">
						{formatMessage(messages.relatedContent, { numRelated: urls.length })}
					</Text>
				) : null}
				<Box xcss={styles.links}>
					<LinkList
						openLinksInNewTab={isInferredProjectEntityAnnotationEnabled()}
						onLinkClick={(_url, _index, options) => {
							if (!options?.isContextMenu) {
								onInteraction?.('source_document');
							}
						}}
						size="compact"
						maxSources={MAX_SOURCES}
						expandedMaxSources={EXPANDED_MAX_SOURCES}
						titles={titles}
						urls={urls}
					/>
				</Box>
			</Stack>
		</Box>
	);
};
